/**
 * WinnerRotatorPanel — split-test analytics widget for the Campaigns dashboard.
 *
 * Reads from /api/winner-rotator and surfaces per-offer variant comparison.
 * "Pause Loser" calls /api/creatives/{id}/pause (Cipher-only — does NOT
 * touch Meta; Nick takes that action manually).
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtUsd = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);
const fmtPct = (n) => (n == null ? "—" : `${(Number(n) * 100).toFixed(2)}%`);
const fmtInt = (n) => (n == null ? "—" : Number(n).toLocaleString());

/**
 * Build the scaling-brief prompt that gets dispatched into TinyTimChat.
 * Pure-frontend so we don't hit the 60s preview-ingress wall on the
 * synchronous Sonnet call.
 */
const buildScalingBriefPrompt = (offer, winner) => {
  const m = winner?.metrics || {};
  const cpa = m.cpa != null ? `$${Number(m.cpa).toFixed(2)}` : "(unknown)";
  const spend = m.spend != null ? `$${Number(m.spend).toFixed(2)}` : "(unknown)";
  const convs = m.conversions != null ? String(m.conversions) : "(unknown)";
  const workspace = offer?.is_abh ? "ABH" : "AFFILIATE";
  const naming = offer?.is_abh
    ? "ABH-<PURPOSE>-<CHANNEL>-<PHASE>"
    : "Owner[-Initials]-OfferName-IMAGE-H#B#C#";
  const creatives = (winner?.creatives || [])
    .slice(0, 5)
    .map((c) => `\n  - ${c.creative_name || c.creative_id} (${c.creative_type || "IMAGE"})`)
    .join("");

  return [
    "Generate a SCALING BRIEF for 3 budget-doubled scaling campaigns based on this confirmed winner.",
    "Use the winning hook + landing-page variant verbatim — this is what's already converting.",
    "",
    `WORKSPACE: ${workspace}`,
    `OFFER: ${offer?.offer_name || offer?.offer_id} (id: ${offer?.offer_id})`,
    `WINNING VARIANT: ${winner?.label || winner?.variant_id} (${winner?.variant_id})`,
    `CURRENT CPA: ${cpa}`,
    `CURRENT SPEND: ${spend}`,
    `CURRENT CONVERSIONS: ${convs}`,
    `WINNING CREATIVES:${creatives || " (none recorded)"}`,
    "",
    "OUTPUT — three campaigns, each with:",
    `  • campaign_name following Cipher convention (${naming})`,
    "  • daily_budget = 2× the implied current per-campaign spend (or $200/day if unknown)",
    "  • audience tweak (lookalike / interest expansion / age slice — pick what makes sense for this offer)",
    "  • bid_strategy + KPI target (CPA cap 10% above winner)",
    "  • creative reuse plan — same hook + LP variant; allow 1 thumbnail/copy variant per campaign",
    "  • risk callout — what would make you kill it in week one",
    "",
    "Be direct. Three campaigns, ready to launch tomorrow.",
  ].join("\n");
};

export default function WinnerRotatorPanel({ defaultDays = 14 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(defaultDays);
  const [error, setError] = useState(null);
  const [pausing, setPausing] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/winner-rotator?days=${days}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePauseCreatives = async (creativeIds, label) => {
    if (!creativeIds || creativeIds.length === 0) return;
    if (!window.confirm(`Pause ${creativeIds.length} losing creative(s) for ${label} in Cipher? (Meta is unchanged — pause manually in Ads Manager.)`)) {
      return;
    }
    setPausing(label);
    try {
      for (const id of creativeIds) {
        await axios.post(`${API}/creatives/${id}/pause`, {
          reason: `winner-rotator: lost split test (${label})`,
        });
      }
      await fetchData();
    } catch (e) {
      setError(e?.response?.data?.detail || "Pause failed");
    } finally {
      setPausing(null);
    }
  };

  const offers = data?.offers || [];
  const minSpend = data?.min_spend_threshold ?? 50;

  return (
    <div
      className="bg-slate-900/80 border border-slate-800 rounded-xl p-5"
      data-testid="winner-rotator-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white">Winner Rotator</h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Split-test winners across both workspaces · min spend ${minSpend} per variant
            · ≥10% CPA margin
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200"
            data-testid="winner-rotator-days"
          >
            <option value={7}>7d</option>
            <option value={14}>14d</option>
            <option value={30}>30d</option>
          </select>
          <button
            onClick={fetchData}
            className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-xs text-slate-200"
            data-testid="winner-rotator-refresh"
          >
            Refresh
          </button>
        </div>
      </div>

      {loading && (
        <div className="text-sm text-slate-400" data-testid="winner-rotator-loading">
          Loading split tests…
        </div>
      )}
      {error && (
        <div className="text-sm text-red-300" data-testid="winner-rotator-error">
          {error}
        </div>
      )}
      {!loading && !error && offers.length === 0 && (
        <div className="text-sm text-slate-400" data-testid="winner-rotator-empty">
          No split tests running. Set <code className="text-emerald-300">landing_page_variant</code> on
          two or more creatives to start tracking a winner.
        </div>
      )}

      <div className="space-y-4">
        {offers.map((offer) => {
          const losers = (offer.variants || []).filter(
            (v) => offer.winner_variant_id && v.variant_id !== offer.winner_variant_id,
          );
          const isWinner = offer.status === "WINNER";
          const isInconclusive = offer.status === "INCONCLUSIVE";
          const isTie = offer.status === "TIE";
          return (
            <div
              key={offer.offer_id}
              className="bg-slate-800/50 border border-slate-700/60 rounded-lg p-4"
              data-testid={`winner-rotator-offer-${offer.offer_id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm font-semibold text-white">
                    {offer.offer_name}
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono">
                    {offer.is_abh ? "ABH" : "AFFILIATE"} · {offer.offer_id}
                  </div>
                </div>
                <span
                  className={`text-[11px] font-mono px-2 py-1 rounded uppercase tracking-wider ${
                    isWinner
                      ? "bg-emerald-500/20 text-emerald-300 border border-emerald-700/50"
                      : isTie
                      ? "bg-amber-500/20 text-amber-300 border border-amber-700/50"
                      : "bg-slate-600/30 text-slate-300 border border-slate-700"
                  }`}
                  data-testid={`winner-rotator-status-${offer.offer_id}`}
                >
                  {offer.status}
                </span>
              </div>

              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-400 border-b border-slate-700/50">
                    <th className="py-1.5 text-left font-medium">Variant</th>
                    <th className="py-1.5 text-right font-medium">Spend</th>
                    <th className="py-1.5 text-right font-medium">Convs</th>
                    <th className="py-1.5 text-right font-medium">CPA</th>
                    <th className="py-1.5 text-right font-medium">CTR</th>
                    <th className="py-1.5 text-right font-medium">Creatives</th>
                  </tr>
                </thead>
                <tbody>
                  {(offer.variants || []).map((v) => {
                    const isWinningRow = isWinner && v.variant_id === offer.winner_variant_id;
                    return (
                      <tr
                        key={v.variant_id}
                        className={`border-b border-slate-800/50 ${
                          isWinningRow ? "bg-emerald-900/15" : ""
                        }`}
                        data-testid={`winner-rotator-variant-${offer.offer_id}-${v.variant_id}`}
                      >
                        <td className="py-1.5 text-slate-200 font-mono">
                          {isWinningRow && (
                            <span className="text-emerald-400 mr-1">★</span>
                          )}
                          {v.label || v.variant_id}
                        </td>
                        <td className="py-1.5 text-right text-slate-200">
                          {fmtUsd(v.metrics?.spend)}
                        </td>
                        <td className="py-1.5 text-right text-slate-200">
                          {fmtInt(v.metrics?.conversions)}
                        </td>
                        <td className="py-1.5 text-right text-slate-200 font-medium">
                          {fmtUsd(v.metrics?.cpa)}
                        </td>
                        <td className="py-1.5 text-right text-slate-200">
                          {fmtPct(v.metrics?.ctr)}
                        </td>
                        <td className="py-1.5 text-right text-slate-400">
                          {(v.creatives || []).length}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {isWinner && losers.length > 0 && (
                <div className="mt-3 flex justify-end gap-2">
                  <button
                    onClick={() => {
                      const winner = (offer.variants || []).find(
                        (v) => v.variant_id === offer.winner_variant_id,
                      );
                      const prompt = buildScalingBriefPrompt(offer, winner);
                      window.dispatchEvent(
                        new CustomEvent("cipher:tinytim-prompt", { detail: { prompt } }),
                      );
                    }}
                    className="px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-500 text-xs text-white font-medium"
                    data-testid={`winner-rotator-scaling-${offer.offer_id}`}
                    title="Open Tiny Tim with a pre-loaded scaling brief for this winner"
                  >
                    Generate Scaling Brief
                  </button>
                  <button
                    onClick={() => {
                      const ids = losers.flatMap((l) =>
                        (l.creatives || [])
                          .filter((c) => c.status !== "paused" && c.status !== "retired")
                          .map((c) => c.creative_id),
                      );
                      handlePauseCreatives(
                        ids,
                        losers.map((l) => l.label || l.variant_id).join(", "),
                      );
                    }}
                    disabled={pausing != null}
                    className="px-3 py-1.5 rounded bg-rose-600/90 hover:bg-rose-500 disabled:opacity-50 text-xs text-white font-medium"
                    data-testid={`winner-rotator-pause-${offer.offer_id}`}
                  >
                    {pausing ? "Pausing…" : `Pause loser${losers.length > 1 ? "s" : ""}`}
                  </button>
                </div>
              )}
              {isInconclusive && (
                <div className="mt-2 text-[11px] text-slate-500 italic">
                  Need more spend (≥${minSpend} per variant) before a winner can be declared.
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
